import type { Vec2 } from '../../core/math';
import { playerGeometry } from '../player/geometry';
import type { WeaponDef } from './schema';

export type WeaponAnchor = Vec2 & { angle: number; flip: boolean };

type PlayerWeaponPose = Vec2 & { ducking: boolean; height?: number };

export function weaponShoulder(pose: PlayerWeaponPose): Vec2 {
  const height = pose.height ?? 1.8;
  return { x: pose.x, y: pose.y - height / 2 + height * (1.06 / 1.8) * playerGeometry(height, pose.ducking).poseScale };
}

/** Aim the bore through a world target. Forward grip/barrel length does not change the line;
 * the shoulder height and mirrored barrel offset do. Very close targets use the nearest angle. */
export function aimWeaponAt(pose: PlayerWeaponPose, weapon: Pick<WeaponDef, 'shape' | 'twoHanded'>, target: Vec2): Vec2 {
  const shoulder = weaponShoulder(pose);
  const dx = target.x - shoulder.x;
  const dy = target.y - shoulder.y;
  const distance = Math.hypot(dx, dy);
  if (distance < 1e-6) return { x: 1, y: 0 };
  const lateral = weaponMuzzle(weapon.shape.kind, weapon.shape.length).y;
  const offset = Math.asin(Math.min(1, Math.max(-1, lateral / distance)));
  const bearing = Math.atan2(dy, dx);
  let angle = bearing - (dx < 0 ? -offset : offset);
  // Directly underneath the shoulder the left/right grip mirror has a narrow seam: neither
  // oblique direction can cross it. Aim vertically with the nearest side's bore offset.
  if ((Math.cos(angle) < 0) !== (dx < 0)) angle = -Math.PI / 2 + (dx < 0 ? -1 : 1) * 1e-12;
  return { x: Math.cos(angle), y: Math.sin(angle) };
}

/** The grip is gameplay geometry: decorative body/hand springs must not move the barrel. */
export function heldWeaponAnchor(pose: {
  x: number;
  y: number;
  aimX: number;
  aimY: number;
  ducking: boolean;
  twoHanded: boolean;
  height?: number;
}): WeaponAnchor {
  const length = Math.hypot(pose.aimX, pose.aimY);
  const aimX = length ? pose.aimX / length : 1;
  const aimY = length ? pose.aimY / length : 0;
  const reach = pose.twoHanded ? 0.52 : 0.58;
  const shoulder = weaponShoulder(pose);
  return { x: shoulder.x + aimX * reach, y: shoulder.y + aimY * reach, angle: Math.atan2(aimY, aimX), flip: aimX < 0 };
}

/** Barrel exits in the silhouette's grip-local frame, including the rounded barrel end. */
export function weaponMuzzle(kind: string, length: number): Vec2 {
  switch (kind) {
    case 'return-disc':
      return { x: 0.545, y: 0 };
    case 'office-stapler':
      return { x: length * 0.9 + 0.015, y: 0.018 };
    case 'office-keyboard':
      return { x: length * 0.86, y: 0 };
    case 'deagle':
    case 'god-pistol':
    case 'ice-gun':
    case 'snake-gun':
      return { x: length * 1.02, y: 0.045 };
    case 'ak47':
    case 'm16':
    case 'm1':
    case 'sniper':
    case 'laser':
    case 'lava-spike-gun':
    case 'lava-beam':
      return { x: length * 0.98 + 0.035, y: 0.02 };
    case 'sawed-off':
    case 'military-shotgun':
    case 'snake-shotgun':
      return { x: length * 0.98 + 0.04, y: 0.045 };
    case 'minigun':
    case 'snake-minigun':
      return { x: length * 0.98 + 0.035, y: 0 };
    case 'snake-launcher':
    case 'flying-snake':
      return { x: length * 0.88 + 0.09, y: 0.03 };
    case 'snake-grenade':
    case 'grenade-launcher':
    case 'bouncer':
    case 'lava-spike-ball':
      return { x: length * 0.88 + 0.12, y: 0.03 };
    case 'rpg':
      return { x: length * 0.9 + 0.23, y: 0.03 };
    case 'lava-stream':
    case 'lava-spray':
    case 'flamethrower':
    case 'thruster':
    case 'siphon':
      return { x: length * 0.98 + 0.055, y: 0.02 };
    case 'time-bubble':
    case 'black-hole':
      return { x: length * 0.82 + 0.11, y: 0 };
    case 'spear':
      return { x: length * 0.56 + 0.31, y: 0 };
    case 'sword':
      return { x: 0.06 + length * 0.8 + 0.045, y: 0 };
    case 'blink-dagger':
      return { x: 0.06 + length * 0.85 + 0.035, y: 0 };
    default:
      return { x: length * 0.82, y: 0.02 };
  }
}

/** Mirror around the barrel axis, then rotate and translate; shared by drawing and firing. */
export function weaponPoint(point: Vec2, anchor: WeaponAnchor): Vec2 {
  const c = Math.cos(anchor.angle);
  const s = Math.sin(anchor.angle);
  const y = anchor.flip ? -point.y : point.y;
  return { x: anchor.x + point.x * c - y * s, y: anchor.y + point.x * s + y * c };
}

/** A freshly fired tracer cannot extend backwards beyond the point where it was spawned. */
export function projectileTrailLength(speed: number, tick: number, spawnTick: number, tickRate: number): number {
  return Math.min(3.08, spawnTick < 0 ? Infinity : speed * Math.max(0, tick - spawnTick) / tickRate);
}
